import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Empty, Select, Table } from "antd";
import DeleteModal from "../../../components/modelpopup/DeleteModal";
import { convertDateFormate } from "../../../utils/convertDateFormate";
import { formatDecimal } from "../../../utils/formatDecimal";
import { OverlayTrigger, Tooltip, Modal, Button } from "react-bootstrap";
import { saveAs } from "file-saver";
import SalaryDetail from "../../../components/SalaryDetail";
import api from "../../../api/axios";
import toast from "react-hot-toast";
import { companyID } from "../../../auth";
import {
  getEmployeeSalaryDetails,
  updateEmployeeSalaryPaymentStatus,
} from "../../../services/salaryServices";

const EmployeeSalaryDetail = ({ selectedEmployeeDetail }) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [salaryId, setSalaryId] = useState(null);
  const [salaryModalLoading, setSalaryModalLoading] = useState(true);
  const [salaryData, setSalaryData] = useState({});
  const [showSlipModal, setShowSlipModal] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [currentSlipName, setCurrentSlipName] = useState("");
  const [salaryRecords, setSalaryRecords] = useState([]);
  const [summary, setSummary] = useState({
    total_salary: 0,
    total_net_salary: 0,
    total_records: 0,
  });
  const [tableLoading, setTableLoading] = useState(false);
  const [updatingPaymentStatusId, setUpdatingPaymentStatusId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ column: "month", order: "desc" });

  const employeeId = localStorage.getItem("employeeID");
  const company_id = companyID();

  const selectedEmployee = selectedEmployeeDetail?.[0] || {};

  const currentFinancialYear = useMemo(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    const startYear = month >= 4 ? year : year - 1;
    return `${startYear}-${String(startYear + 1).slice(-2)}`;
  }, []);

  const [financialYear, setFinancialYear] = useState(currentFinancialYear);
  const financialYearOptions = useMemo(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const month = today.getMonth() + 1;
    const currentStartYear = month >= 4 ? currentYear : currentYear - 1;
    const startYear = 2020;

    return Array.from({ length: currentStartYear - startYear + 1 }, (_, index) => {
      const year = startYear + index;
      return `${year}-${String(year + 1).slice(-2)}`;
    });
  }, []);

  useEffect(() => {
    if (selectedEmployee?.employee_id) {
      setSortConfig({ column: "month", order: "desc" });
    }
  }, [selectedEmployee?.employee_id]);

  useEffect(() => {
    const fetchSalaryDetails = async () => {
      if (!company_id || !selectedEmployee?.employee_id) {
        setSalaryRecords([]);
        setSummary({ total_salary: 0, total_net_salary: 0, total_records: 0 });
        return;
      }

      try {
        setTableLoading(true);
        const response = await getEmployeeSalaryDetails(
          company_id,
          selectedEmployee.employee_id,
          financialYear || currentFinancialYear,
          sortConfig.column,
          sortConfig.order
        );

        setSalaryRecords(response?.data || []);
        setSummary(
          response?.summary || {
            total_salary: 0,
            total_net_salary: 0,
            total_records: 0,
          }
        );
      } catch (error) {
        console.error(error);
        setSalaryRecords([]);
        setSummary({ total_salary: 0, total_net_salary: 0, total_records: 0 });
      } finally {
        setTableLoading(false);
      }
    };

    fetchSalaryDetails();
  }, [company_id, selectedEmployee?.employee_id, financialYear, currentFinancialYear, sortConfig]);

  const handleCloseModal = () => {
    setShowModal(false);
    // setEmployId('');
  };

  const handleCloseSlipModal = () => {
    setShowSlipModal(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }
    setPdfBlob(null);
    setPdfUrl(null);
    setCurrentSlipName("");
  };

  const handleDownloadSlip = () => {
    if (pdfBlob && currentSlipName) {
      saveAs(pdfBlob, currentSlipName);
      toast.success("Salary slip downloaded successfully!");
      handleCloseSlipModal();
    } else {
      toast.error("Unable to download salary slip");
    }
  };

  const deleteSalary = () => {
    setLoading(true);
    api
      .put(`employee/salary-detail/${salaryId}`, {
        updated_by: employeeId,
      })
      .then((res) => {
        console.log(res.data.message);
        setSalaryId(null);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
        // loader
      });
  };

  const handleTableChange = (_, __, sorter) => {
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextColumn = activeSorter?.columnKey || "month";

    if (Array.isArray(sorter)) {
      setSortConfig({
        column: nextColumn,
        order:
          activeSorter?.order === "ascend"
            ? "asc"
            : activeSorter?.order === "descend"
              ? "desc"
              : sortConfig.column === nextColumn && sortConfig.order === "asc"
                ? "desc"
                : "asc",
      });
      return;
    }

    setSortConfig({
      column: nextColumn,
      order:
        activeSorter?.order === "ascend"
          ? "asc"
          : activeSorter?.order === "descend"
            ? "desc"
            : sortConfig.column === nextColumn && sortConfig.order === "asc"
              ? "desc"
              : "asc",
    });
  };

  const normalizePaymentStatus = (status) => {
    const value = String(status || "UNPAID").toUpperCase();
    return value === "PAID" ? "PAID" : "UNPAID";
  };

  const getPaymentStatusBadgeStyles = (status) => {
    const normalizedStatus = normalizePaymentStatus(status);
    const isPaid = normalizedStatus === "PAID";
    const backgroundColor = isPaid ? "#16a34a" : "#facc15";
    const textColor = isPaid ? "#ffffff" : "#111827";

    return {
      width: "fit-content",
      minWidth: "unset",
      height: 22,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "0 10px",
      borderRadius: 999,
      fontWeight: 600,
      fontSize: 11,
      whiteSpace: "nowrap",
      border: "none",
      boxShadow: "none",
      textAlign: "center",
      backgroundColor,
      color: textColor,
      cursor: "pointer",
      userSelect: "none",
      lineHeight: 1,
    };
  };

  const handlePaymentStatusChange = async (salaryId, paymentStatus) => {
    if (!salaryId || !paymentStatus) {
      return;
    }

    try {
      setUpdatingPaymentStatusId(salaryId);
      await updateEmployeeSalaryPaymentStatus(salaryId, paymentStatus, employeeId);
      toast.success("Payment status updated successfully");

      const response = await getEmployeeSalaryDetails(
        company_id,
        selectedEmployee.employee_id,
        financialYear || currentFinancialYear,
        sortConfig.column,
        sortConfig.order
      );

      setSalaryRecords(response?.data || []);
      setSummary(
        response?.summary || {
          total_salary: 0,
          total_net_salary: 0,
          total_records: 0,
        }
      );
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.error || "Failed to update payment status");
    } finally {
      setUpdatingPaymentStatusId(null);
    }
  };

  const getSortOrder = (columnKey) => {
    if (sortConfig.column !== columnKey) {
      return null;
    }

    return sortConfig.order === "asc" ? "ascend" : "descend";
  };

  const columns = [
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: getSortOrder("month"),
      render: (text) => {
        const monthYear = convertDateFormate(text);
        return (
          new Date(monthYear.year, monthYear.month - 1).toLocaleString(
            "default",
            { month: "long" }
          ) +
          " " +
          monthYear.year
        );
      },
    },
    {
      title: "Total Salary",
      dataIndex: "total_salary",
      key: "total_salary",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: getSortOrder("total_salary"),
      render: (text) => formatDecimal(text),
    },
    {
      title: "Reimbursement",
      dataIndex: "reimbursement_allowance",
      key: "reimbursement_allowance",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: getSortOrder("reimbursement_allowance"),
      render: (text) => formatDecimal(text),
    },
    {
      title: "Other deduction",
      dataIndex: "other_deduction",
      key: "other_deduction",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: getSortOrder("other_deduction"),
      render: (text) => formatDecimal(text),
    },
    // {
    //   title: "Other Deduction Reason",
    //   dataIndex: "other_deduction_reason",
    //   key: "other_deduction_reason",
    //   render:(text)=> (text || '-')
    // },
    {
      title: "Total Deduction",
      dataIndex: "total_deduction",
      key: "total_deduction",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: getSortOrder("total_deduction"),
      render: (text) => formatDecimal(text),
    },
    {
      title: "Net Salary",
      dataIndex: "net_salary",
      key: "net_salary",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: getSortOrder("net_salary"),
      render: (text) => formatDecimal(text),
    },
    {
      title: "Payment Status",
      dataIndex: "payment_status",
      key: "payment_status",
      render: (_, record) => {
        const currentStatus = normalizePaymentStatus(record.payment_status);

        return (
            <div style={{ position: "relative", display: "inline-flex" }}>
            <span style={getPaymentStatusBadgeStyles(currentStatus)}>
              {currentStatus === "PAID" ? "Paid" : "Unpaid"}
            </span>
            <Select
              size="small"
              value={currentStatus}
              variant="borderless"
              disabled={updatingPaymentStatusId === record.id}
              onChange={(value) => handlePaymentStatusChange(record.id, value)}
              options={[
                { label: "Paid", value: "PAID" },
                { label: "Unpaid", value: "UNPAID" },
              ]}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                opacity: 0,
                cursor: "pointer",
              }}
              popupMatchSelectWidth={false}
            />
          </div>
        );
      },
    },
    {
      title: "Action",
      render: (text, record) => (
        <div className="action-menu text-end">
          <div className="d-flex gap-2 mt-2">
            <Link
              className="dropdown-item"
              onClick={(e) => {
                e.preventDefault();
                setLoading(true);
                api
                  .get(`${record.salary_slip}`, {
                    responseType: "blob",
                  })
                  .then((res) => {
                    const blob = new Blob([res.data], {
                      type: "application/pdf",
                    });
                    const url = URL.createObjectURL(blob);
                    setPdfBlob(blob);
                    setPdfUrl(url);
                    setCurrentSlipName(record.salary_slip);
                    setShowSlipModal(true);
                  })
                  .catch((err) => {
                    console.log(err);
                    toast.error("Failed to load salary slip");
                  })
                  .finally(() => {
                    setLoading(false);
                  });
              }}
            >
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip
                    id="tooltip-view"
                    style={{ fontSize: "12px", padding: "2px" }}
                  >
                    View Salary Slip
                  </Tooltip>
                }
              >
                <i className="fa fa-eye m-r-5 text-info" />
              </OverlayTrigger>
            </Link>
            {/* <Link
              className="dropdown-item"
              onClick={(e) => {
                e.preventDefault();
                setShowModal(true);
                setSalaryId(record.id);
                console.log(" Testing 4", record);
                setSalaryData({
                  ...record,
                  totalPay: record.total_salary,
                  otherAllowance: record.other_allowance,
                  medicalAllowance: record.medical_allowance,
                  statutoryBonus: record.statutory_bonus,
                });
              }}
            >
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip
                    id="tooltip-view"
                    style={{ fontSize: "12px", padding: "2px" }}
                  >
                    Edit Salary
                  </Tooltip>
                }
              >
                <i className="fa fa-pencil m-r-5 text-success" />
              </OverlayTrigger>
            </Link> */}
            <Link
              className="dropdown-item"
              data-bs-toggle="modal"
              data-bs-target="#delete_employee"
              onClick={(e) => {
                e.preventDefault();
                setSalaryId(record.id);
              }}
            >
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip
                    id="tooltip-view"
                    style={{ fontSize: "12px", padding: "2px" }}
                  >
                    Delete Salary
                  </Tooltip>
                }
              >
                <i className="fa fa-trash m-r-5 text-danger" />
              </OverlayTrigger>
            </Link>
          </div>
        </div>
      ),
    },
  ];
  return (
    <div>
      {/* <EmployeeListFilter /> */}
      <div className="row">
        <div className="col-md-12">
          <div className="d-flex flex-wrap gap-3 align-items-end justify-content-between mb-3">
            <div>
              <div className="text-muted mb-1">Financial Year</div>
              <Select
                value={financialYear}
                style={{ width: 180 }}
                onChange={(value) => {
                  setFinancialYear(value);
                  setSortConfig({ column: "month", order: "desc" });
                }}
                options={financialYearOptions.map((year) => ({
                  label: year,
                  value: year,
                }))}
              />
            </div>

            <div className="d-flex flex-wrap gap-3">
              <div className="border rounded px-3 py-2 bg-white">
                <div className="text-muted small">Total Salary</div>
                <div className="fw-semibold fs-6">{formatDecimal(summary.total_salary)}</div>
              </div>
              <div className="border rounded px-3 py-2 bg-white">
                <div className="text-muted small">Total Net Salary</div>
                <div className="fw-semibold fs-6">{formatDecimal(summary.total_net_salary)}</div>
              </div>
              <div className="border rounded px-3 py-2 bg-white">
                <div className="text-muted small">Records</div>
                <div className="fw-semibold fs-6">{summary.total_records}</div>
              </div>
            </div>
          </div>
          <div className="table-responsive">
            {salaryRecords.length > 0 || tableLoading ? (
              <Table
                className="table-striped"
                columns={columns}
                dataSource={salaryRecords}
                rowKey={(record) => record.id}
                loading={tableLoading}
                onChange={handleTableChange}
                pagination={false}
              />
            ) : (
              <div className="py-4">
                <Empty description="No salary details available for the selected financial year." />
              </div>
            )}
          </div>
        </div>
      </div>
      {/* /Page Content */}
      {/* <AllEmployeeAddPopup /> */}
      <SalaryDetail
        show={showModal}
        handleClose={handleCloseModal}
        loading={salaryModalLoading}
        setLoading={setSalaryModalLoading}
        salaryId={salaryId}
        salaryData={salaryData}
      />
      <DeleteModal Name="Delete Salary" confirmDelete={deleteSalary} />
      
      {/* Salary Slip Viewer Modal */}
      <Modal show={showSlipModal} onHide={handleCloseSlipModal} size="lg" centered>
        <Modal.Header>
          <Modal.Title>Salary Slip</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pdfUrl ? (
            <div style={{ height: "70vh", overflow: "auto" }}>
              <iframe
                src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                width="100%"
                height="100%"
                title="Salary Slip"
                style={{ border: "none" }}
              />
            </div>
          ) : (
            <div className="text-center p-4">
              <p>Loading salary slip...</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseSlipModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleDownloadSlip}
            disabled={!pdfBlob}
          >
            <i className="fa fa-download me-2" />
            Download
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EmployeeSalaryDetail;
